import { useCallback, useState } from 'react';
import type { Workflow } from '@/components/ApprovalStageWizard/types';

export interface UseApprovalDialogStateParams {
  workflows: Workflow[];
}

export interface UseApprovalDialogStateResult {
  assignDialogOpen: boolean;
  workflowToAssign: string | null;
  handleAssignWorkflow: (workflowId: string, e: React.MouseEvent) => void;
  handleAssignOpenChange: (open: boolean) => void;
  duplicateDialogOpen: boolean;
  workflowToDuplicate: Workflow | null;
  handleDuplicateWorkflow: (workflowId: string, e: React.MouseEvent) => void;
  handleDuplicateOpenChange: (open: boolean) => void;
  closeDuplicateDialog: () => void;
  deleteDialogOpen: boolean;
  workflowToDelete: string | null;
  openDeleteDialog: (workflowId: string) => void;
  closeDeleteDialog: () => void;
  handleDeleteOpenChange: (open: boolean) => void;
}

/**
 * Owns the open/close state and target workflow for the assign, duplicate and
 * delete dialogs on the approval workflows page.
 */
export const useApprovalDialogState = ({
  workflows,
}: UseApprovalDialogStateParams): UseApprovalDialogStateResult => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [workflowToAssign, setWorkflowToAssign] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [workflowToDuplicate, setWorkflowToDuplicate] = useState<Workflow | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);

  const handleAssignWorkflow = useCallback((workflowId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setWorkflowToAssign(workflowId);
    setAssignDialogOpen(true);
  }, []);

  const handleAssignOpenChange = useCallback((open: boolean) => {
    setAssignDialogOpen(open);
    if (!open) {
      setWorkflowToAssign(null);
    }
  }, []);

  const handleDuplicateWorkflow = useCallback((workflowId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow) return;
    setWorkflowToDuplicate(workflow);
    setDuplicateDialogOpen(true);
  }, [workflows]);

  const handleDuplicateOpenChange = useCallback((open: boolean) => {
    setDuplicateDialogOpen(open);
    if (!open) {
      setWorkflowToDuplicate(null);
    }
  }, []);

  const closeDuplicateDialog = useCallback(() => {
    setDuplicateDialogOpen(false);
    setWorkflowToDuplicate(null);
  }, []);

  const openDeleteDialog = useCallback((workflowId: string) => {
    setWorkflowToDelete(workflowId);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
  }, []);

  const handleDeleteOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  }, []);

  return {
    assignDialogOpen,
    workflowToAssign,
    handleAssignWorkflow,
    handleAssignOpenChange,
    duplicateDialogOpen,
    workflowToDuplicate,
    handleDuplicateWorkflow,
    handleDuplicateOpenChange,
    closeDuplicateDialog,
    deleteDialogOpen,
    workflowToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    handleDeleteOpenChange,
  };
};
